import { useEffect, useState } from "react";
import Calendar from "react-calendar";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";

import "../../calendar/Calendar.css";

function NotificationMethodRow({
    icon,
    title,
    description,
    checked,
    onChange,
    name,
    divided = false,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                borderTop: divided ? "1px solid" : "none",
                borderColor: "divider",
            }}
        >
            <Box sx={{ color: "text.secondary", display: "grid" }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={onChange}
                name={name}
                inputProps={{ "aria-label": title }}
            />
        </Box>
    );
}

export default function FileNotificationsDialog(props) {
    const [sendNotifications, setSendNotifications] = useState(false);
    const [sendEmails, setSendEmails] = useState(false);
    const [message, setMessage] = useState("");
    const [fromDate, setFromDate] = useState(dayjs());

    useEffect(() => {
        if (props.open) {
            setSendNotifications(Boolean(props.send));
            setSendEmails(Boolean(props.email));
            setMessage(props.message || "");
            setFromDate(props.fromDate || dayjs());
        }
    }, [props.open, props.send, props.email, props.message, props.fromDate]);

    const handleClose = () => {
        props.onClose({
            sendNotifications: sendNotifications,
            message: message,
            fromDate: fromDate,
            sendEmails: sendEmails,
        });
    };

    const handleChangeMessageText = (event) => {
        setMessage(event.target.value);
    };

    function handleSwitchChange(event) {
        if (event.target.name === "send") {
            setSendNotifications(event.target.checked);
        } else if (event.target.name === "email") {
            setFromDate(dayjs());
            setSendEmails(event.target.checked);
        }
    }

    function onFromDateChange(nextValue) {
        setFromDate(nextValue);
    }

    const selectedDate = dayjs.isDayjs(fromDate)
        ? fromDate.toDate()
        : fromDate;

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="upload-notifications-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="sm"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "92vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <NotificationsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="upload-notifications-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Notifications
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose how people should hear about this file.
                    </Typography>
                </Box>
                {props.recipientCount > 0 && (
                    <Chip
                        label={`${props.recipientCount} ${
                            props.recipientCount === 1
                                ? "recipient"
                                : "recipients"
                        }`}
                        size="small"
                        sx={{ display: { xs: "none", sm: "flex" } }}
                    />
                )}
                <IconButton
                    aria-label="Close notifications"
                    onClick={handleClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                >
                    Delivery
                </Typography>
                <Box
                    sx={{
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                    }}
                >
                    <NotificationMethodRow
                        icon={<NotificationsOutlinedIcon />}
                        title="In-platform notification"
                        description="Show a notification when users open Nodiant."
                        checked={sendNotifications}
                        onChange={handleSwitchChange}
                        name="send"
                    />
                    <NotificationMethodRow
                        icon={<EmailOutlinedIcon />}
                        title="Email"
                        description="Send an email to eligible recipients."
                        checked={sendEmails}
                        onChange={handleSwitchChange}
                        name="email"
                        divided
                    />
                </Box>

                <Box
                    sx={{
                        mt: 2,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 800 }}
                    >
                        Message
                    </Typography>
                    <TextField
                        id="upload-notification-message"
                        label="Optional message"
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={8}
                        value={message}
                        onChange={handleChangeMessageText}
                        fullWidth
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Box>

                {sendEmails && (
                    <Box
                        sx={{
                            mt: 2,
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.25,
                            }}
                        >
                            <CalendarMonthOutlinedIcon
                                fontSize="small"
                                color="primary"
                            />
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800 }}
                            >
                                Email date
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1.5 }}
                        >
                            Choose today to send immediately, or a future date
                            to schedule the email.
                        </Typography>
                        <Box sx={notificationCalendarSx}>
                            <Calendar
                                value={selectedDate}
                                onChange={onFromDateChange}
                                minDate={new Date()}
                                defaultView="month"
                                locale="en-GB"
                                prev2Label={null}
                                next2Label={null}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleClose}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const notificationCalendarSx = {
    "& .react-calendar": {
        width: "100%",
        border: "none",
        boxShadow: "none",
        color: "text.primary",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        lineHeight: 1.25,
    },
    "& .react-calendar button": {
        border: "none",
        fontFamily: "inherit",
    },
    "& .react-calendar__navigation": {
        height: 42,
        marginBottom: 0.5,
        display: "flex",
        gap: 0.5,
    },
    "& .react-calendar__navigation button": {
        borderRadius: "9px !important",
        fontWeight: 800,
    },
    "& .react-calendar__navigation button:enabled:hover, & .react-calendar__navigation button:enabled:focus":
        {
            backgroundColor: "action.hover",
        },
    "& .react-calendar__navigation button[disabled], & .react-calendar__tile:disabled":
        {
            color: "text.disabled",
            backgroundColor: "action.disabledBackground",
        },
    "& .react-calendar__month-view__weekdays": {
        color: "text.secondary",
        fontSize: 11,
        fontWeight: 800,
    },
    "& .react-calendar__month-view__weekdays abbr": {
        textDecoration: "none",
    },
    "& .react-calendar__tile": {
        minHeight: 42,
        padding: "8px 3px",
        borderRadius: "9px !important",
        color: "text.primary",
    },
    "& .react-calendar__month-view__days__day--neighboringMonth": {
        color: "text.disabled",
    },
    "& .react-calendar__month-view__days__day--weekend": {
        color: "text.primary",
    },
    "& .react-calendar__tile:enabled:hover, & .react-calendar__tile:enabled:focus":
        {
            backgroundColor: "action.hover",
        },
    "& .react-calendar__tile--now": {
        color: "primary.main",
        backgroundColor: "action.hover",
        fontWeight: 800,
    },
    "& .react-calendar__tile--active, & .react-calendar__tile--active:enabled:hover, & .react-calendar__tile--active:enabled:focus":
        {
            color: "white",
            backgroundColor: "primary.dark",
        },
};
